import React, {
  useReducer,
  useEffect,
  useRef,
  forwardRef,
  useState,
  useMemo,
  memo,
  useCallback,
  useLayoutEffect,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { getWidgetConfig } from "@/services/api/widget";
import { SaveConfigButton } from "./SaveConfigButton";

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/ui/table/Table";
import type { Document } from "@/services/api/documents/types";
import type { PaginatedResponse } from "@/services/api/types";
import { format } from "date-fns";
import { ErrorBanner } from "@/components/ErrorBanner";
import { isAccessDeniedError, ACCESS_DENIED_ERROR } from "@/utils/errorUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileImage,
  faCloud,
  faTimes,
  faCaretDown,
  faHashtag,
  faSearch,
  faTags,
  faSparkles,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import { DocumentEditorDrawer } from "./DocumentEditorDrawer";
import { getDocument, getDocumentTags } from "@/services/api/documents";
import {
  getFileTypeIcon,
  getReadableFileType,
  formatFileSize,
} from "@/utils/files";
import { downloadDocument } from "@/utils/documentDownload";
import { EditablePill } from "@/components/common/EditablePill";
import { TagDropdown } from "./TagDropdown";
import { useNotch } from "@/context/NotchContext";
import styles from "./DocumentsList.module.css";
import { Button } from "@/components/ui/button/Button";
import { WORKBENCH_SEARCH_PILL_CLASS } from "@/components/ui/workbench";

// Define the state type
type State = {
  documents: Document[];
  loading: boolean;
  error: string | null;
  refreshCounter: number;
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  } | null;
};

// Define the action types
type Action =
  | { type: "FETCH_START" }
  | {
      type: "FETCH_SUCCESS";
      payload: { documents: Document[]; pagination: State["pagination"] };
    }
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "REFRESH" };

// Define the reducer function
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return {
        ...state,
        loading: false,
        documents: action.payload.documents,
        pagination: action.payload.pagination,
      };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "REFRESH":
      return {
        ...state,
        loading: true,
        refreshCounter: state.refreshCounter + 1,
      };
    default:
      return state;
  }
}

export type DocumentColumnKey =
  | "name"
  | "type"
  | "size"
  | "uploadedBy"
  | "uploaded";

const DEFAULT_COLUMNS: DocumentColumnKey[] = ["name", "type", "uploaded"];

interface DocumentsListProps {
  entityId?: string;
  entityType?: "worker" | "team" | "department" | string;
  fetchDocuments: (
    entityId?: string,
    params?: { tags?: string } & {
      page?: number;
      page_size?: number;
      include_provider_products?: boolean;
    }
  ) => Promise<PaginatedResponse<Document>>;
  websocketMessageType?: string;
  title?: string;
  visibleColumns?: DocumentColumnKey[];
  hideSuggestedFilters?: boolean;
  hideSearch?: boolean;
  hidePagination?: boolean;
  maxItems?: number;
  disableDocumentClick?: boolean;
  onDocumentClick?: (document: Document) => void;
  showProviderToggle?: boolean; // New prop to show/hide the provider toggle
}

// Define the ref type
export interface DocumentsListRef {
  refreshDocuments: () => void;
}

// Add debounce utility
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Add this type at the top with other types
type Pill = {
  type: "search" | "tag";
  value: string;
  id: string;
  isNew?: boolean;
};

// Move PillsContainer definition here, above DocumentsList
const PillsContainer = memo(function PillsContainer({
  pills,
  onRemovePill,
  removingPillIds,
  newPillIds,
}: {
  pills: Pill[];
  onRemovePill: (idx: number) => void;
  removingPillIds: Set<string>;
  newPillIds: Set<string>;
}) {
  const memoizedPills = useMemo(() => pills, [pills]);
  return (
    <div className="flex flex-col gap-2">
      <motion.div
        className="flex gap-2 mt-1 overflow-x-auto pb-2 no-scrollbar"
        layout
        transition={{
          layout: {
            duration: 0.3,
            delay: 0.2,
          },
        }}
      >
        <AnimatePresence mode="popLayout">
          {memoizedPills.map((pill, idx) => (
            <motion.span
              key={pill.id}
              layout
              initial={
                newPillIds.has(pill.id) ? { opacity: 0, scale: 0.8 } : false
              }
              animate={{ opacity: 1, scale: 1 }}
              exit={{
                opacity: 0,
                scale: 0.8,
                transition: { duration: 0.3 },
              }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 30,
                mass: 1,
              }}
              style={{ transformOrigin: "center" }}
              className="flex-shrink-0"
            >
              <EditablePill
                text={pill.value}
                icon={
                  <FontAwesomeIcon
                    icon={pill.type === "search" ? faSearch : faHashtag}
                  />
                }
                onIconChange={() => {}}
                onColorChange={() => {}}
                isActive={false}
                disableIconEdit={true}
                showRemoveButton={true}
                onRemove={() => onRemovePill(idx)}
                isNew={newPillIds.has(pill.id)}
              />
            </motion.span>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
});

// Re-add SearchInput definition here
const SearchInput = memo(function SearchInput({
  value,
  onChange,
  onKeyDown,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder="Search documents or type # for tags..."
      className={`px-3 py-1 rounded-xl h-10 w-full text-slate-900 placeholder:text-slate-500 dark:text-white dark:placeholder:text-slate-400 ${WORKBENCH_SEARCH_PILL_CLASS}`}
    />
  );
});

// Add smart truncation function
function smartTruncate(filename: string, maxLength: number = 38): string {
  if (filename.length <= maxLength) return filename;
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) {
    return filename.slice(0, maxLength - 3) + "...";
  }
  const extension = filename.slice(lastDotIndex);
  const name = filename.slice(0, lastDotIndex);
  const charsToKeep = Math.floor(maxLength * 0.7);
  if (name.length <= charsToKeep) return filename;
  const firstPart = name.slice(0, charsToKeep);
  const remainingLength = maxLength - charsToKeep - extension.length - 3;
  const lastPart = name.slice(-remainingLength);
  return `${firstPart}...${lastPart}${extension}`;
}

// Add DocumentsTable component definition
interface DocumentsTableProps {
  documents: Document[];
  visibleColumns: DocumentColumnKey[];
  onDocumentClick: (document: Document) => void;
  isLoading?: boolean;
  disableClick?: boolean;
}

const DocumentsTable = memo(function DocumentsTable({
  documents,
  visibleColumns,
  onDocumentClick,
  isLoading = false,
  disableClick = false,
}: DocumentsTableProps) {
  const showColumn = (key: DocumentColumnKey) => visibleColumns.includes(key);

  return (
    <Table dense>
      <TableHead>
        <TableRow>
          {showColumn("name") && <TableHeader>Name</TableHeader>}
          {showColumn("type") && <TableHeader>Type</TableHeader>}
          {showColumn("size") && <TableHeader>Size</TableHeader>}
          {showColumn("uploadedBy") && <TableHeader>Uploaded By</TableHeader>}
          {showColumn("uploaded") && <TableHeader>Uploaded</TableHeader>}
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          // Show loading placeholders
          [...Array(5)].map((_, rowIndex) => (
            <TableRow key={`loading-row-${rowIndex}`}>
              {visibleColumns.map((column) => (
                <TableCell key={`loading-${rowIndex}-${column}`}>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 animate-pulse rounded" />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : documents.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={visibleColumns.length}
              className="text-center py-8"
            >
              <div className="text-gray-500 dark:text-gray-400">
                No documents found
              </div>
            </TableCell>
          </TableRow>
        ) : (
          documents.map((document) => (
            <TableRow
              key={document.document_id}
              className={`${
                !disableClick
                  ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors duration-150"
                  : ""
              }`}
              onClick={
                !disableClick ? () => onDocumentClick(document) : undefined
              }
            >
              {showColumn("name") && (
                <TableCell className="max-w-0 w-full">
                  <div className="flex items-center w-full min-w-0 gap-3">
                    <div
                      className={`flex-shrink-0 h-full flex items-center justify-center my-auto ${
                        !disableClick &&
                        (document.file_type === "cloud_document" ||
                          document.metadata?.user?.file_id ||
                          document.metadata?.file_id)
                          ? "transition-transform duration-150 hover:scale-110"
                          : ""
                      }`}
                    >
                      {getFileTypeIcon(document.file_type || "")}
                    </div>
                    <div className="flex-grow min-w-0 w-full">
                      <FilenameTruncate
                        filename={
                          document.display_name ||
                          document.filename ||
                          "Unnamed Document"
                        }
                        className={`text-sm min-w-0 w-full truncate ${
                          !disableClick &&
                          (document.file_type === "cloud_document" ||
                            document.metadata?.user?.file_id ||
                            document.metadata?.file_id)
                            ? "text-slate-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline cursor-pointer transition-colors duration-150"
                            : "text-slate-900 dark:text-white"
                        }`}
                      />
                    </div>
                  </div>
                </TableCell>
              )}
              {showColumn("type") && (
                <TableCell className="text-sm text-slate-600 dark:text-gray-400">
                  {getReadableFileType(document.file_type || "")}
                </TableCell>
              )}
              {showColumn("size") && (
                <TableCell className="text-sm text-slate-600 dark:text-gray-400">
                  {formatFileSize(document.size || 0)}
                </TableCell>
              )}
              {showColumn("uploadedBy") && (
                <TableCell className="text-sm text-slate-600 dark:text-gray-400">
                  {document.user_id ? document.user_id : "-"}
                </TableCell>
              )}
              {showColumn("uploaded") && (
                <TableCell className="text-sm text-slate-600 dark:text-gray-400">
                  {document.upload_date
                    ? format(new Date(document.upload_date), "MMM d, yyyy")
                    : "-"}
                </TableCell>
              )}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
});

// Add FilenameTruncate component
function FilenameTruncate({
  filename,
  className,
}: {
  filename: string;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxChars, setMaxChars] = useState(30);

  useLayoutEffect(() => {
    function updateMaxChars() {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        // Estimate average character width for text-sm (about 8px)
        const chars = Math.max(10, Math.floor(width / 7));
        setMaxChars(chars);
      }
    }
    updateMaxChars();
    window.addEventListener("resize", updateMaxChars);
    return () => window.removeEventListener("resize", updateMaxChars);
  }, []);

  return (
    <div
      ref={containerRef}
      className={"min-w-0 w-full truncate " + (className || "")}
    >
      {smartTruncate(filename, maxChars)}
    </div>
  );
}

// Add helper function for page numbers
const getPageNumbers = (currentPage: number, totalPages: number) => {
  const pages: (number | string)[] = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);
    if (currentPage <= 3) {
      endPage = 4;
    }
    if (currentPage >= totalPages - 2) {
      startPage = totalPages - 3;
    }
    if (startPage > 2) {
      pages.push("...");
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    if (endPage < totalPages - 1) {
      pages.push("...");
    }
    pages.push(totalPages);
  }
  return pages;
};

export const DocumentsList = forwardRef<DocumentsListRef, DocumentsListProps>(
  function DocumentsList(
    {
      entityId,
      entityType,
      fetchDocuments,
      title,
      visibleColumns = DEFAULT_COLUMNS,
      hideSuggestedFilters,
      hideSearch,
      hidePagination,
      maxItems,
      disableDocumentClick,
      onDocumentClick,
      showProviderToggle = false,
    },
    ref
  ): React.ReactElement {
    // Add search params hook
    const [searchParams] = useSearchParams();
    const configId = searchParams.get("config");

    // Initialize state with config if present
    const getInitialPills = useCallback(async () => {
      if (!configId) return [];

      try {
        const widgetConfig = await getWidgetConfig(configId);
        const config = widgetConfig.config;
        const newPills: Pill[] = [];

        if (config.filters) {
          // Add tag pills
          if (config.filters.tags && Array.isArray(config.filters.tags)) {
            config.filters.tags.forEach((tag: string) => {
              newPills.push({
                type: "tag",
                value: tag,
                id: `tag-${tag}-${Date.now()}`,
              });
            });
          }

          // Add search term pills
          if (
            config.filters.search_terms &&
            Array.isArray(config.filters.search_terms)
          ) {
            config.filters.search_terms.forEach((term: string) => {
              newPills.push({
                type: "search",
                value: term,
                id: `search-${term}-${Date.now()}`,
              });
            });
          }
        }

        return newPills;
      } catch (error) {
        console.error("Error loading config:", error);
        return [];
      }
    }, [configId]);

    // Initialize state
    const [pills, setPills] = useState<Pill[]>([]);
    const [isInitializing, setIsInitializing] = useState(true);

    // Initialize state with config
    useEffect(() => {
      const initialize = async () => {
        const initialPills = await getInitialPills();
        setPills(initialPills);
        setIsInitializing(false);
      };
      initialize();
    }, [getInitialPills]);

    // Restore reducer and state
    const initialState: State = {
      documents: [],
      loading: true,
      error: null,
      refreshCounter: 0,
      pagination: null,
    };
    const [state, dispatch] = useReducer(reducer, initialState);

    // Restore refs for lifecycle and loading
    const isMounted = useRef(true);
    const isLoadingRef = useRef(false);
    const prevDropdownOpenRef = useRef(false);
    const [userNavigated, setUserNavigated] = useState(false);

    // Add notch detection
    const { isNotchActive } = useNotch();

    // Add state for document editor
    const [selectedDocument, setSelectedDocument] = useState<Document | null>(
      null
    );
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editorContent, setEditorContent] = useState<any>(null);

    // Replace separate searchTerms and selectedTags with a unified pills array
    const [removingPillIds, setRemovingPillIds] = useState<Set<string>>(
      new Set()
    );
    const [newPillIds, setNewPillIds] = useState<Set<string>>(new Set());

    // State for tag filtering
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [availableTags, setAvailableTags] = useState<string[]>([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [tagsError, setTagsError] = useState<string | null>(null);

    // State for tag search input
    const [tagSearch, setTagSearch] = useState("");
    const [highlightedTagIndex, setHighlightedTagIndex] = useState<number>(-1);

    // State to manage tag visibility
    const [showAllTags, setShowAllTags] = useState(false);

    // State for smart search
    const [searchInput, setSearchInput] = useState("");
    const [tagMode, setTagMode] = useState(false);

    // Add pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(maxItems || 100);

    // Add state for showing all suggested tags
    const [showAllSuggestedTags, setShowAllSuggestedTags] = useState(false);

    // Add state for provider toggle
    const [includeProviderProducts, setIncludeProviderProducts] =
      useState(false);

    // Track initial mount
    const didMount = useRef(false);

    // Get random tags
    const getRandomTags = useCallback((tags: string[], count: number) => {
      const shuffled = [...tags].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, count);
    }, []);

    // Memoize random tags
    const randomTags = useMemo(() => {
      return getRandomTags(availableTags, 2);
    }, [availableTags, getRandomTags]);

    // Handle pill removal
    const handleRemovePill = useCallback(
      (idx: number) => {
        const pillToRemove = pills[idx];
        if (!pillToRemove) return;

        setRemovingPillIds((prev) => new Set([...prev, pillToRemove.id]));
        setTimeout(() => {
          setPills((prev) => prev.filter((p) => p.id !== pillToRemove.id));
          setRemovingPillIds((prev) => {
            const next = new Set(prev);
            next.delete(pillToRemove.id);
            return next;
          });

          // Reset to first page when filters change
          setCurrentPage(1);
        }, 300);
      },
      [pills]
    );

    const handleAddTagPill = useCallback((tag: string) => {
      const newId = `${tag}-${Date.now()}`;
      setPills((prev) => {
        if (prev.some((p) => p.type === "tag" && p.value === tag)) return prev;
        return [...prev, { type: "tag", value: tag, id: newId }];
      });
      // Add the new pill's id to newPillIds for animation
      setNewPillIds((prev) => new Set(prev).add(newId));
      // Timeout: After 300ms (the animation duration), remove the id from newPillIds
      // This is used with Framer Motion to control the 'pop-in' animation for new pills only
      setTimeout(() => {
        setNewPillIds((prev) => {
          const next = new Set(prev);
          next.delete(newId);
          return next;
        });
      }, 300);
      setCurrentPage(1);
    }, []);

    // Handle adding a search term pill
    const handleAddSearchTermPill = useCallback((term: string) => {
      const newId = `search-${term}-${Date.now()}`;
      setPills((prev) => {
        return [...prev, { type: "search", value: term, id: newId }];
      });
      setNewPillIds((prev) => new Set(prev).add(newId));
      setTimeout(() => {
        setNewPillIds((prev) => {
          const next = new Set(prev);
          next.delete(newId);
          return next;
        });
      }, 300);
      setCurrentPage(1);
    }, []);

    // Memoize the pills container
    const memoizedPillsContainer = useMemo(
      () => (
        <PillsContainer
          pills={pills}
          onRemovePill={handleRemovePill}
          removingPillIds={removingPillIds}
          newPillIds={newPillIds}
        />
      ),
      [pills, handleRemovePill, removingPillIds, newPillIds]
    );

    // Memoize filtered tags calculation
    const filteredTags = useMemo(() => {
      if (!tagSearch) return [];
      const searchLower = tagSearch.toLowerCase();
      return availableTags.filter((tag) =>
        tag.toLowerCase().includes(searchLower)
      );
    }, [availableTags, tagSearch]);

    // Memoize visible tags
    const visibleTags = useMemo(() => {
      return filteredTags.slice(0, 8);
    }, [filteredTags]);

    // Memoize filtered visible tags
    const filteredVisibleTags = useMemo(() => {
      const selectedTagValues = pills
        .filter((p) => p.type === "tag")
        .map((p) => p.value);
      return visibleTags.filter((tag) => !selectedTagValues.includes(tag));
    }, [visibleTags, pills]);

    // Effect: Reset highlight index when filtered tags change
    useEffect(() => {
      if (filteredVisibleTags.length === 0) {
        setHighlightedTagIndex(-1);
      } else if (highlightedTagIndex >= filteredVisibleTags.length) {
        setHighlightedTagIndex(0);
      }
    }, [filteredVisibleTags, highlightedTagIndex]);

    // Handle smart tag selection
    const handleSmartTagSelect = useCallback(
      (tag: string) => {
        if (!pills.some((p) => p.type === "tag" && p.value === tag)) {
          handleAddTagPill(tag);
          setSearchInput("");
          setTagMode(false);
          setTagSearch("");
          setHighlightedTagIndex(-1);
          setShowAllSuggestedTags(false);
        }
      },
      [handleAddTagPill, pills]
    );

    // Memoize the tag dropdown component
    const MemoizedTagDropdown = useMemo(() => {
      if (!tagMode || !tagSearch || filteredVisibleTags.length === 0) {
        return null;
      }
      return (
        <TagDropdown
          visibleTags={filteredVisibleTags}
          highlightedTagIndex={highlightedTagIndex}
          onSelect={handleSmartTagSelect}
          onMouseEnterIndex={setHighlightedTagIndex}
        />
      );
    }, [
      tagMode,
      tagSearch,
      filteredVisibleTags,
      highlightedTagIndex,
      handleSmartTagSelect,
    ]);

    // Memoize the search input handler
    const handleSearchInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);

        // Batch state updates
        const match = value.match(/(?:^|\s)#(\w*)$/);
        const isTagMode = !!match;
        const tagSearchValue = match ? match[1] : "";

        // Use a single state update for related values
        React.startTransition(() => {
          setTagMode(isTagMode);
          setTagSearch(tagSearchValue);
          if (isTagMode && filteredVisibleTags.length > 0) {
            setHighlightedTagIndex(0);
          } else {
            setHighlightedTagIndex(-1);
          }
        });
      },
      [filteredVisibleTags]
    );

    // Memoize the tag search keydown handler
    const handleTagSearchKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (tagMode) {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedTagIndex((prev) => {
              if (filteredVisibleTags.length === 0) return -1;
              return prev === filteredVisibleTags.length - 1 ? 0 : prev + 1;
            });
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedTagIndex((prev) => {
              if (filteredVisibleTags.length === 0) return -1;
              return prev <= 0 ? filteredVisibleTags.length - 1 : prev - 1;
            });
          } else if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();

            // If there's a highlighted tag, use that
            if (
              highlightedTagIndex >= 0 &&
              filteredVisibleTags[highlightedTagIndex]
            ) {
              handleSmartTagSelect(filteredVisibleTags[highlightedTagIndex]);
            }
            // If there's a tag search value and it's not already a pill, use that
            else if (
              tagSearch &&
              !pills.some((p) => p.type === "tag" && p.value === tagSearch)
            ) {
              handleSmartTagSelect(tagSearch);
            }
            // If there's a tag search value and it matches an available tag, use that
            else if (tagSearch && availableTags.includes(tagSearch)) {
              handleSmartTagSelect(tagSearch);
            }
          }
        } else if (e.key === "Enter" && searchInput.trim() !== "") {
          e.preventDefault();
          const term = searchInput.trim();
          if (
            term &&
            !pills.some((p) => p.type === "search" && p.value === term)
          ) {
            handleAddSearchTermPill(term);
          }
          setSearchInput("");
          setTagMode(false);
          setTagSearch("");
          setHighlightedTagIndex(-1);
          setShowAllSuggestedTags(false);
        }
      },
      [
        tagMode,
        filteredVisibleTags,
        highlightedTagIndex,
        handleSmartTagSelect,
        searchInput,
        pills,
        handleAddSearchTermPill,
        tagSearch,
        availableTags,
      ]
    );

    // Memoize the search input component
    const memoizedSearchInput = useMemo(
      () => (
        <div className="flex items-center gap-2">
          <SearchInput
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyDown={handleTagSearchKeyDown}
          />
        </div>
      ),
      [searchInput, handleSearchInputChange, handleTagSearchKeyDown]
    );

    // Memoize filtered documents calculation
    const filteredDocuments = useMemo(() => {
      return state.documents;
    }, [state.documents]);

    // Handle tag selection
    const handleTagToggle = (tag: string) => {
      setSelectedTags((prev) =>
        prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
      );
    };

    // Handle tag search input change
    const handleTagSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setTagSearch(e.target.value);
    };

    // Handle tag search input key press
    const handleTagSearchKeyPress = (
      e: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setShowAllSuggestedTags(true);
        setHighlightedTagIndex((prev) => {
          if (visibleTags.length === 0) return -1;
          return prev === visibleTags.length - 1 ? 0 : prev + 1;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setShowAllSuggestedTags(true);
        setHighlightedTagIndex((prev) => {
          if (visibleTags.length === 0) return -1;
          return prev <= 0 ? visibleTags.length - 1 : prev - 1;
        });
      } else if (e.key === "Enter") {
        if (highlightedTagIndex >= 0 && visibleTags[highlightedTagIndex]) {
          const tag = visibleTags[highlightedTagIndex];
          handleTagToggle(tag);
          setTagSearch("");
          setHighlightedTagIndex(-1);
          setShowAllSuggestedTags(false);
        } else if (
          tagSearch &&
          !selectedTags.includes(tagSearch) &&
          availableTags.includes(tagSearch)
        ) {
          handleTagToggle(tagSearch);
          setTagSearch("");
          setHighlightedTagIndex(-1);
          setShowAllSuggestedTags(false);
        }
      }
    };

    // Clear all selected tags
    const handleClearTags = () => {
      setSelectedTags([]);
    };

    // Function to load documents
    async function loadDocuments() {
      if (!fetchDocuments || isLoadingRef.current || !isMounted.current) return;

      isLoadingRef.current = true;

      try {
        // Get all tag pills
        const tagPills = pills.filter((p) => p.type === "tag");
        const tags = tagPills.map((p) => p.value);

        // Get all search term pills
        const searchPills = pills.filter((p) => p.type === "search");
        const searchTerms = searchPills.map((p) => p.value);

        // Only include tags parameter if there are tags
        const params = {
          ...(tags.length > 0
            ? {
                tags: tags.join(","),
                match_all_tags: true,
              }
            : {}),
          ...(searchTerms.length > 0
            ? {
                search_terms: searchTerms.join(","),
              }
            : {}),
          page: currentPage,
          page_size: maxItems || pageSize,
          ...(showProviderToggle && entityType === "product"
            ? { include_provider_products: includeProviderProducts }
            : {}),
        };

        // Call fetchDocuments with tags parameter
        const response = await fetchDocuments(entityId, params);

        if (isMounted.current) {
          dispatch({
            type: "FETCH_SUCCESS",
            payload: {
              documents:
                response.results ||
                response.data ||
                (response as any).documents ||
                [],
              pagination: response.pagination
                ? {
                    total: (response.pagination as any).total_items,
                    per_page: (response.pagination as any).page_size,
                    current_page: (response.pagination as any).page,
                    last_page: (response.pagination as any).total_pages,
                  }
                : null,
            },
          });
        }
      } catch (err) {
        console.error("Error loading documents:", err);
        if (isMounted.current) {
          const { isAccessDenied, message } = isAccessDeniedError(err);
          if (isAccessDenied) {
            dispatch({ type: "FETCH_ERROR", payload: ACCESS_DENIED_ERROR });
            return;
          }
          const errorMessage =
            message ||
            (err instanceof Error ? err.message : "Failed to load documents");
          dispatch({ type: "FETCH_ERROR", payload: errorMessage });
        }
      } finally {
        isLoadingRef.current = false;
      }
    }

    // Function to manually trigger a refresh
    function refreshDocuments() {
      if (!isLoadingRef.current && isMounted.current) {
        dispatch({ type: "REFRESH" });
      }
    }

    // Expose the refresh function via ref
    React.useImperativeHandle(
      ref,
      () => ({
        refreshDocuments,
      }),
      []
    );

    // Cleanup on unmount
    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);

    // Effect to handle initial load and refreshes for documents
    useEffect(() => {
      if (isInitializing) return; // Don't load documents until initialization is complete
      loadDocuments();
    }, [
      pills,
      currentPage,
      pageSize,
      entityId,
      state.refreshCounter,
      isInitializing,
      includeProviderProducts,
    ]);

    // Effect to load tags only once on mount
    useEffect(() => {
      loadTags();
    }, []);

    // Effect: Only set highlight to 0 when dropdown first opens and user hasn't navigated
    React.useEffect(() => {
      const dropdownOpen = !!tagSearch && visibleTags.length > 0;
      if (dropdownOpen && !prevDropdownOpenRef.current && !userNavigated) {
        setHighlightedTagIndex(0);
      }
      prevDropdownOpenRef.current = dropdownOpen;
      if (!dropdownOpen) {
        setHighlightedTagIndex(-1);
        setUserNavigated(false);
      }
    }, [tagSearch, visibleTags, userNavigated]);

    // Add handleDocumentClick function
    const handleDocumentClick = async (document: Document) => {
      if (onDocumentClick) {
        onDocumentClick(document);
      } else {
        if (document.file_type === "cloud_document") {
          try {
            const fullDocument = await getDocument(document.document_id);
            setSelectedDocument(fullDocument);
            setEditorContent(fullDocument.body);
            setIsEditorOpen(true);
          } catch (error) {
            console.error("Error fetching document:", error);
          }
        } else {
          try {
            const fileId =
              document.metadata?.user?.file_id || document.metadata?.file_id;
            if (!fileId) {
              console.error("No file_id found in document metadata");
              return;
            }
            const filename =
              document.metadata?.user?.original_filename ||
              document.metadata?.original_filename ||
              document.display_name ||
              document.filename ||
              undefined;
            await downloadDocument(fileId, filename);
          } catch (error) {
            console.error("Error downloading document:", error);
          }
        }
      }
    };

    // Add function to handle editor close
    const handleEditorClose = () => {
      setIsEditorOpen(false);
      setSelectedDocument(null);
      setEditorContent(null);
    };

    // Add function to handle save completion
    const handleSaveComplete = () => {
      handleEditorClose();
      refreshDocuments();
    };

    // Function to load tags
    async function loadTags() {
      if (!isMounted.current) return;

      setTagsLoading(true);
      setTagsError(null);

      try {
        const tags = await getDocumentTags(
          entityType && entityId
            ? {
                resource_type: entityType,
                resource_id: entityId,
              }
            : undefined
        );
        if (isMounted.current) {
          setAvailableTags(tags.sort());
        }
      } catch (err) {
        console.error("Error loading tags:", err);
        if (isMounted.current) {
          setTagsError(
            err instanceof Error ? err.message : "Failed to load tags"
          );
        }
      } finally {
        setTagsLoading(false);
      }
    }

    // Add renderPageNumbers function
    const renderPageNumbers = () => {
      if (!state.pagination) return null;
      const { current_page, last_page } = state.pagination;
      if (!last_page || last_page <= 1) return null;

      const pageNumbersToDisplay = getPageNumbers(current_page, last_page);
      if (!pageNumbersToDisplay || pageNumbersToDisplay.length === 0)
        return null;

      return pageNumbersToDisplay.map((page, index) => {
        const isActive = current_page === page;
        return typeof page === "number" ? (
          <button
            key={`page-${page}-${index}`}
            className={`flex items-center justify-center font-normal text-sm py-1 min-h-0 h-8 w-8 px-0 flex-none transition-colors duration-150 ease-in-out ${
              isActive
                ? "text-indigo-600 dark:text-indigo-400 bg-slate-200 dark:bg-slate-800"
                : "text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-slate-700"
            }`}
            onClick={() => setCurrentPage(page)}
            disabled={state.loading}
          >
            {page}
          </button>
        ) : (
          <span
            key={`ellipsis-${index}`}
            className="leading-none -top-1 relative text-sm text-gray-500 dark:text-gray-400 flex items-center"
          >
            ...
          </span>
        );
      });
    };

    // Add isAniAdmin check
    const [isAniAdmin, setIsAniAdmin] = useState(false);

    useEffect(() => {
      const adminStatus = localStorage.getItem("isAniAdmin") === "true";
      setIsAniAdmin(adminStatus);
    }, []);

    // Render based on state
    if (isInitializing) {
      return (
        <div className="p-4 flex h-full w-full items-center justify-center">
          Loading configuration...
        </div>
      );
    }

    if (state.loading) {
      return (
        <div className="p-4 flex h-full w-full items-center justify-center">
          Loading documents...
        </div>
      );
    }

    if (state.error === ACCESS_DENIED_ERROR) {
      return (
        <div className="h-full flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              You don&apos;t have permission to view documents for this{" "}
              {entityType || "resource"}. Contact your administrator if you
              believe this is an error.
            </p>
          </div>
        </div>
      );
    }

    if (state.error) {
      return (
        <div className="p-4 flex h-full w-full">
          <ErrorBanner message={`Error: ${state.error}`} />
        </div>
      );
    }

    return (
      <div className="flex h-full w-full">
        <div className="w-full">
          {/* Provider Toggle Section */}
          {showProviderToggle && entityType === "product" && (
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={includeProviderProducts}
                    onChange={(e) => {
                      setIncludeProviderProducts(e.target.checked);
                      setCurrentPage(1); // Reset to first page when toggle changes
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  Show all documents for provider
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  (includes documents from all products by the same provider)
                </span>
              </div>
            </div>
          )}

          {/* Search and Tag Filter Section */}
          {!hideSearch && (
            <div className="">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="relative flex-grow">
                    {memoizedSearchInput}
                    {MemoizedTagDropdown}
                  </div>

                  {isAniAdmin && (
                    <SaveConfigButton
                      pills={pills}
                      entityId={entityId}
                      entityType={entityType}
                    />
                  )}

                  {!hideSuggestedFilters && availableTags.length > 0 && (
                    <div className="flex items-center gap-2 ">
                      {randomTags.map((tag) => (
                        <div key={tag} className="cursor-pointer flex-none">
                          <Button
                            gradient={true}
                            gradientColors={["#c084fc", "#a7bffa", "#5af4ff"]}
                            animate={true}
                            fluid
                            onClick={() => handleSmartTagSelect(tag)}
                          >
                            {tag}
                          </Button>
                        </div>
                      ))}
                      {availableTags.length > 2 && (
                        <div
                          className={`cursor-pointer flex-none inline-flex items-center justify-center rounded-xl px-4 text-sm font-medium h-9 focus:outline-none transition-colors duration-150 ease-in-out bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 ${
                            showAllSuggestedTags ? "ring-2 ring-indigo-400" : ""
                          }`}
                          onClick={() =>
                            setShowAllSuggestedTags((showAllSuggestedTags) =>
                              showAllSuggestedTags ? false : true
                            )
                          }
                        >
                          {showAllSuggestedTags ? "Show Less" : "Show All"}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {!hideSuggestedFilters && availableTags.length > 0 && showAllSuggestedTags && (
                  <div className="flex flex-wrap gap-2 w-full">
                    {availableTags
                      .filter((tag) => !randomTags.includes(tag))
                      .map((tag) => (
                        <div key={tag} className="cursor-pointer">
                          <EditablePill
                            text={tag}
                            icon={<FontAwesomeIcon icon={faHashtag} />}
                            currentIcon="hashtag"
                            onIconChange={() => {}}
                            onColorChange={() => {}}
                            onClickPill={() => handleSmartTagSelect(tag)}
                            isActive={pills.some(
                              (p) => p.type === "tag" && p.value === tag
                            )}
                            disableIconEdit={true}
                          />
                        </div>
                      ))}
                  </div>
                )}
                {memoizedPillsContainer}
              </div>
            </div>
          )}

          {state.documents.length === 0 ? (
            <div className="p-4 w-full h-full flex items-center justify-center text-gray-400">
              <div className="flex justify-between items-center mb-4">
                <span>No documents available</span>
              </div>
            </div>
          ) : (
            <>
              <div className={`${!hidePagination ? "pb-20" : ""}`}>
                <DocumentsTable
                  documents={state.documents}
                  visibleColumns={visibleColumns}
                  onDocumentClick={handleDocumentClick}
                  isLoading={state.loading}
                  disableClick={disableDocumentClick}
                />
              </div>

              {/* Pagination Controls */}
              {!hidePagination &&
                state.pagination &&
                state.pagination.total > 0 && (
                  <div
                    className={`fixed bottom-4 left-4 right-4 flex justify-between z-40 items-center transition-[margin] duration-300 ease-in-out font-mono text-slate-600 dark:text-gray-400 space-x-2 backdrop-blur-xl rounded-md shadow-lg border border-slate-200 bg-white/90 dark:border-transparent dark:bg-gray-800/90 p-2 py-3 ${
                      isNotchActive ? "mr-20" : "mr-0"
                    }`}
                  >
                    <div className="flex items-center justify-between space-x-2 px-4 w-full">
                      <span className="text-sm text-slate-700 dark:text-gray-300">
                        {state.pagination.total} result
                        {state.pagination.total === 1 ? "" : "s"}
                      </span>
                      <div className="flex items-center space-x-1 font-mono">
                        <button
                          className={`min-h-0 w-8 h-8 flex items-center font-mono font-normal justify-center ${
                            currentPage === 1 ? "opacity-0" : "opacity-100"
                          }`}
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={currentPage === 1 || state.loading}
                        >
                          &larr;
                        </button>
                        <div className="flex items-center space-x-1">
                          {renderPageNumbers()}
                        </div>
                        <button
                          className={`w-8 h-8 flex items-center font-mono font-normal justify-center min-h-0 ${
                            currentPage === state.pagination.last_page
                              ? "opacity-0"
                              : "opacity-100"
                          }`}
                          onClick={() => setCurrentPage((prev) => prev + 1)}
                          disabled={
                            currentPage === state.pagination.last_page ||
                            state.loading
                          }
                        >
                          &rarr;
                        </button>
                      </div>
                      <select
                        className="mr-2 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1); // Reset to page 1 when size changes
                        }}
                        disabled={state.loading}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>
                  </div>
                )}
            </>
          )}

          {/* Add the DocumentEditorDrawer */}
          {selectedDocument && (
            <DocumentEditorDrawer
              isOpen={isEditorOpen}
              onClose={handleEditorClose}
              initialContent={editorContent}
              onSaveComplete={handleSaveComplete}
              document={selectedDocument}
              resourceMetadata={
                selectedDocument.metadata.resource_type && selectedDocument.metadata.resource_id
                  ? {
                      resource_type: selectedDocument.metadata.resource_type,
                      resource_id: selectedDocument.metadata.resource_id,
                    }
                  : undefined
              }
            />
          )}
        </div>
      </div>
    );
  }
);
